/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '../services/api';

const AdminContext = createContext(null);

// Custom hook to use admin context
const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

// Admin provider component
const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if admin is already logged in on app start
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const token = adminAPI.getToken();
        if (token) {
          // Verify token is still valid
          const response = await adminAPI.verify();
          setAdmin({ ...response.admin, permissions: response.admin.permissions || [] });
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Admin auth check failed:', error);
        // Token is invalid, remove it
        adminAPI.logout();
        setIsAuthenticated(false);
        setAdmin(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  const login = useCallback(async (username, password, totpCode) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await adminAPI.login(username, password, totpCode);

      if (response.requires2FA) return { requires2FA: true };

      setAdmin({ ...response.user, permissions: response.user.permissions || [] });
      setIsAuthenticated(true);

      return { success: true, admin: response.user };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Admin login failed';
      setError(errorMessage);
      setIsAuthenticated(false);
      setAdmin(null);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    adminAPI.logout();
    setAdmin(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = useMemo(() => ({
    admin,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError
  }), [admin, isAuthenticated, isLoading, error, login, logout, clearError]);

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export { useAdmin, AdminProvider };